// Service d'authentification pour gérer les tokens JWT
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.user = JSON.parse(localStorage.getItem('authUser') || 'null');
  }

  // Connexion
  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur de connexion');
      }

      // Stocker le token et les informations utilisateur
      this.token = data.token;
      this.user = data.user;
      
      localStorage.setItem('authToken', this.token);
      localStorage.setItem('authUser', JSON.stringify(this.user));

      return data;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  }

  // Déconnexion
  async logout() {
    try {
      // Appeler l'API de déconnexion si nécessaire
      if (this.token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le stockage local
      this.token = null;
      this.user = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    }
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // Obtenir le token actuel
  getToken() {
    return this.token;
  }

  // Obtenir les informations utilisateur
  getUser() {
    return this.user;
  }

  // Vérifier si l'utilisateur a une permission spécifique
  hasPermission(permission) {
    return this.user && this.user.permissions && this.user.permissions.includes(permission);
  }

  // Vérifier si l'utilisateur a un rôle spécifique
  hasRole(role) {
    return this.user && this.user.role === role;
  }

  // Vérifier la validité du token
  async verifyToken() {
    if (!this.token) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Token invalide, nettoyer
        this.logout();
        return false;
      }

      const data = await response.json();
      
      // Mettre à jour les informations utilisateur si nécessaire
      if (data.user) {
        this.user = data.user;
        localStorage.setItem('authUser', JSON.stringify(this.user));
      }

      return true;
    } catch (error) {
      console.error('Erreur de vérification du token:', error);
      this.logout();
      return false;
    }
  }

  // Obtenir les headers d'authentification pour les requêtes API
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Faire une requête authentifiée
  async authenticatedFetch(url, options = {}) {
    const authHeaders = this.getAuthHeaders();
    
    const config = {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Si la réponse indique que le token est invalide
      if (response.status === 401 || response.status === 403) {
        const data = await response.json();
        if (data.code === 'NO_TOKEN' || data.code === 'INVALID_TOKEN') {
          this.logout();
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
      }

      return response;
    } catch (error) {
      console.error('Erreur lors de la requête authentifiée:', error);
      throw error;
    }
  }

  // Obtenir les informations de l'utilisateur connecté depuis l'API
  async getCurrentUser() {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/auth/me`);
      
      if (!response.ok) {
        throw new Error('Impossible de récupérer les informations utilisateur');
      }

      const data = await response.json();
      
      // Mettre à jour les informations utilisateur
      this.user = data.user;
      localStorage.setItem('authUser', JSON.stringify(this.user));
      
      return data.user;
    } catch (error) {
      console.error('Erreur lors de la récupération des informations utilisateur:', error);
      throw error;
    }
  }
}

// Instance singleton du service d'authentification
const authService = new AuthService();

export default authService; 